import AccountHelper from 'features/Account/accountHelper';
import AgencyHelper from 'features/Agency/agencyHelper';
import AuthHelper from 'features/Auth/authHelper';
import { resetAuthSlice } from 'features/Auth/authSlice';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { resetAgencyState } from 'features/ManageDealer/agencySlice';
import SystemHelper from 'features/System/systemHelper';
import feApi from 'api/feApi';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTooltip, { KTTooltipPositions } from 'general/components/OtherKeenComponents/KTTooltip';
import AdvanceNavMenu from 'general/components/quybka/Others/AdvanceNavMenu';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import PreferenceKeys from 'general/constants/PreferenceKeys';
import UIHelper from 'general/helpers/UIHelper';
import RoleUtils from 'general/utils/RoleUtils';
import Utils from 'general/utils/Utils';
import useOnClickOutside from 'hooks/useOnClickOutside';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

ButtonAccountDropdownInfo.propTypes = {};

function ButtonAccountDropdownInfo(props) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [pendingWithdrawAmount, setPendingWithdrawAmount] = useState(0);
  const [freshAgencyBalance, setFreshAgencyBalance] = useState(null);
  const [freshWalletBalance, setFreshWalletBalance] = useState(null);
  const [freshAvailableCommission, setFreshAvailableCommission] = useState(null);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const agencyId = user?.agencyId || user?.agency?.agencyId;
  const agencyBalance = Number(freshAgencyBalance ?? user?.agency?.balance ?? 0);
  const walletBalance = Number(freshWalletBalance ?? agencyBalance ?? 0);
  const availableBalance = Math.max(Number(freshAvailableCommission ?? agencyBalance ?? 0), 0);
  const canSwitchToCustomerCms = !isAdmin;
  const handleSwitchToCustomerCms = () => {
    setShowDropdown(false);
    localStorage.setItem(PreferenceKeys.savedUiMode, RoleUtils.uiModes.customer);
    window.location.assign(AppRoute.customerHome);
  };
  const userRoleName = useMemo(() => {
    if (user?.role !== ChargeConstants.UserRole.AGENCY) {
      return ChargeHelper.Utils.getUserRoleName(user?.role);
    } else {
      if (!user?.agency?.parentAgencyId) {
        return 'Tổng đại lý';
      }
      return AgencyHelper.Utils.getAgencyRoleName(user?.agency?.primaryRole);
    }
  }, [user]);
  const [showDropdown, setShowDropdown] = useState(false);
  const refDropdown = useRef(null);
  useEffect(() => {
    if (isAdmin || !agencyId) {
      setPendingWithdrawAmount(0);
      setFreshAgencyBalance(null);
      setFreshWalletBalance(null);
      setFreshAvailableCommission(null);
      return;
    }
    feApi.getAffiliateSummary()
      .then((response) => {
        if (response?.result === 'success') {
          setFreshWalletBalance(Number(response?.summary?.walletBalance ?? 0));
          setFreshAvailableCommission(Number(response?.summary?.availableCommission ?? 0));
          setPendingWithdrawAmount(Number(response?.summary?.withdrawalPending ?? 0));
        }
      })
      .catch(() => {
        setFreshWalletBalance(null);
        setFreshAvailableCommission(null);
      });
    AgencyHelper.API.requestAgencyDetail(agencyId).then((agency) => {
      if (Utils.isValid(agency)) {
        setFreshAgencyBalance(Number(agency?.balance || 0));
      }
    });
    Promise.all([
      AgencyHelper.API.requestGetAgencyWithdrawRequests({
        agencyId,
        status: 'REQUESTED',
        limit: 100,
      }),
      AgencyHelper.API.requestGetAgencyWithdrawRequests({
        agencyId,
        status: 'PROCESSING',
        limit: 100,
      }),
    ]).then(([requested, processing]) => {
      const payments = [
        ...(requested?.payments || []),
        ...(processing?.payments || []),
      ];
      setPendingWithdrawAmount(_.sumBy(payments, (item) => Number(item?.amount || 0)));
    });
  }, [agencyId, isAdmin]);
  useOnClickOutside(refDropdown, () => {
    if (showDropdown) {
      setShowDropdown(false);
    }
  });

  return (
    <div className="position-relative" ref={refDropdown}>
      <div
        className="d-flex align-items-center gap-3 cursor-pointer hover-opacity-60"
        onClick={() => {
          setShowDropdown(!showDropdown);
        }}
      >
        <div className="d-flex flex-column align-items-end">
          <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>
            {user?.fullname ?? ''}
          </span>
          <div className="d-flex align-items-center gap-1">
            <span style={UIHelper.buildFontStyle('400', '14px', AppColors.primary)}>
              {userRoleName}
            </span>
            {!isAdmin && !!user?.agency?.parentAgencyId && (
              <>
                <KTTooltip
                  position={KTTooltipPositions.auto}
                  text={`<span>Số dư ví: ${Utils.formatNumber(walletBalance)} VNĐ<br />Có thể rút: ${Utils.formatNumber(availableBalance)} VNĐ<br />Đang chờ xử lý: ${Utils.formatNumber(pendingWithdrawAmount)} VNĐ<br />Bạn có thể rút tiền về tài khoản thông qua tính năng “Rút tiền về tài khoản”.</span>`}
                  textHeader="Số dư khả dụng của bạn"
                  isHtmlContent
                >
                  <span style={UIHelper.buildFontStyle('400', '14px', AppColors.primary)}>
                    • Khả dụng {Utils.formatNumber(availableBalance)} VNĐ
                  </span>
                </KTTooltip>
              </>
            )}
          </div>
        </div>
        <div>
          {UIHelper.buildAvatar(
            user?.avatar ? Utils.getFullUrl(user?.avatar) : null,
            40,
            AppResource.icons.avatar.default,
            false,
            false,
            10
          )}
        </div>
      </div>
      {/* dropdown info */}
      <>
        <div
          key={showDropdown}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          {showDropdown && (
            <div
              className="bg-white position-absolute top-100 right-0 shadow"
              style={{
                borderRadius: '5px',
                minWidth: '240px',
                marginTop: '10px',
              }}
            >
              {/* caret */}
              <div
                className="dropdown-caret position-absolute"
                style={{
                  top: '-10px',
                  right: '20px',
                  width: '0',
                  height: '0',
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: `10px solid white`,
                }}
              ></div>
              {/* header */}
              <div className="d-flex flex-column align-items-start p-4 gap-3">
                <div className="d-flex align-items-center gap-3 w-100">
                  <div
                    className="flex-shrink-0"
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: `1px solid ${AppColors.gray[200]}`,
                    }}
                  >
                    {UIHelper.buildAvatar(
                      user?.avatar ? Utils.getFullUrl(user?.avatar) : null,
                      62,
                      AppResource.icons.avatar.default,
                      false,
                      false,
                      2
                    )}
                  </div>
                  <div className="d-flex flex-column gap-1 min-w-0 flex-grow-1">
                    <span
                      style={{
                        ...UIHelper.buildFontStyle('600', '16px', AppColors.gray[900]),
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.fullname ?? ''}
                    </span>
                    <span
                      style={{
                        ...UIHelper.buildFontStyle('400', '14px', AppColors.gray[800]),
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.email ?? ''}
                    </span>
                    <KTLabel
                      text={`${userRoleName} CMS`}
                      isInline={true}
                      size={KTLabelSize.medium}
                      additionalClassName={`label-light-success `}
                    />
                  </div>
                </div>
                {canSwitchToCustomerCms && (
                  <button
                    type="button"
                    onClick={handleSwitchToCustomerCms}
                    className="border-0 w-100 d-flex align-items-center text-left"
                    style={{
                      minHeight: '74px',
                      gap: '12px',
                      borderRadius: '16px',
                      background: '#f0fdf4',
                      color: '#15803d',
                      padding: '12px 14px',
                    }}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#dcfce7',
                        color: '#16a34a',
                      }}
                    >
                      <i className="fa-solid fa-arrow-right-arrow-left" />
                    </span>
                    <span className="d-flex flex-column min-w-0 flex-grow-1">
                      <strong
                        style={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Chuyển sang CMS Khách hàng
                      </strong>
                      <small
                        style={{
                          display: 'block',
                          color: '#64748b',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Sử dụng giao diện và tính năng cho khách hàng
                      </small>
                    </span>
                    <i className="fa-solid fa-chevron-right flex-shrink-0" />
                  </button>
                )}
              </div>
              {/* menu */}
              <div
                className="py-2"
                style={{
                  borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
              >
                <AdvanceNavMenu
                  itemList={[
                    {
                      text: 'Cài đặt tài khoản',
                      iconElement: <i className="fa-regular fa-user" />,
                      active: false,
                      disabled: false,
                      onClick: () => {
                        setShowDropdown(false);
                        SystemHelper.UI.Account.showDialogUpdateAccountInfo(user, () => {
                          AccountHelper.callApiGetProfile();
                        });
                      },
                    },
                    {
                      text: 'Đổi mật khẩu',
                      iconElement: <i className="fa-regular fa-lock" />,
                      active: false,
                      disabled: false,
                      onClick: () => {
                        setShowDropdown(false);
                        SystemHelper.UI.Account.showDialogChangePassword(() => { });
                      },
                    },
                    {
                      text: 'Đăng xuất',
                      iconElement: <i className="fa-regular fa-right-from-bracket" />,
                      active: false,
                      disabled: false,
                      onClick: () => {
                        setShowDropdown(false);
                        SystemHelper.UI.Account.showDialogConfirmSignOut(() => {
                          AuthHelper.signOut();
                          dispatch(resetAuthSlice());
                          dispatch(resetAgencyState());
                          router.navigate(AppRoute.auth);
                        });
                      },
                    },
                  ]}
                  enableHoverStyle={true}
                  enableActiveStyle={true}
                />
              </div>
            </div>
          )}
        </div>
      </>
    </div>
  );
}

export default ButtonAccountDropdownInfo;
